import {
  ChatResponseSchema,
  MeResponseSchema,
  SpeakResponseSchema,
  TranscribeSessionResponseSchema,
  UserPreferencesSchema,
  TOOLS,
  type ChatRequest,
  type ChatResponse,
  type ConfirmRequest,
  type LanguageCode,
  type MeResponse,
  type SpeakRequest,
  type SpeakResponse,
  type ToolInput,
  type ToolName,
  type ToolOutput,
  type TranscribeSessionRequest,
  type TranscribeSessionResponse,
  type UserPreferences,
  type VoiceSessionReport,
} from '@ampshift/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface HealthResponse {
  ok: boolean;
  region: string;
  version: string;
}

export interface ApiClientOptions {
  /** Base URL; empty uses the Vite proxy (`/v1` → http://localhost:8787). */
  baseUrl?: string;
  /** Returns Authorization / X-Dev-Principal / X-Session-Id headers for the current session. */
  getAuthHeaders: () => Record<string, string> | Promise<Record<string, string>>;
  getLocale?: () => LanguageCode;
  /** Called on a 401 that could not be recovered: the session has expired. */
  onUnauthorized?: () => void;
  /** Optional attempt to refresh credentials before retrying a 401 once. Return false to give up. */
  tryRefresh?: () => Promise<boolean>;
  fetchImpl?: typeof fetch;
}

export interface ApiClient {
  health(): Promise<HealthResponse>;
  me(): Promise<MeResponse>;
  updatePreferences(patch: Partial<UserPreferences>): Promise<UserPreferences>;
  chat(req: ChatRequest): Promise<ChatResponse>;
  confirm(req: ConfirmRequest): Promise<ChatResponse>;
  tool<N extends ToolName>(name: N, input: ToolInput<N>): Promise<ToolOutput<N>>;
  transcribeSession(req: TranscribeSessionRequest): Promise<TranscribeSessionResponse>;
  sessionReport(report: VoiceSessionReport): Promise<{ ok: boolean }>;
  speak(req: SpeakRequest): Promise<SpeakResponse>;
  fhirPatient(patientId: string): Promise<Record<string, unknown>>;
  fhirEverything(patientId: string): Promise<Record<string, unknown>>;
}

interface ErrorBody {
  error?: { code?: unknown; message?: unknown; details?: unknown };
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody | undefined;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    body = undefined;
  }
  const code = typeof body?.error?.code === 'string' ? body.error.code : `HTTP_${res.status}`;
  const message = typeof body?.error?.message === 'string' ? body.error.message : res.statusText || `Request failed (${res.status})`;
  return new ApiError(res.status, code, message, body?.error?.details ?? null);
}

/**
 * A newer API may add surface types or enum values before this bundle is refreshed. Validate the envelope
 * strictly, but let individual surfaces through: the renderer re-validates each one and degrades gracefully.
 */
function lenientChatResponse(raw: unknown): ChatResponse {
  const strict = ChatResponseSchema.safeParse(raw);
  if (strict.success) return strict.data;
  const envelope = ChatResponseSchema.omit({ surfaces: true }).safeParse(raw);
  const surfaces = Array.isArray((raw as { surfaces?: unknown })?.surfaces) ? ((raw as { surfaces: unknown[] }).surfaces as ChatResponse['surfaces']) : [];
  if (envelope.success) return { ...envelope.data, surfaces };
  throw strict.error;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const base = opts.baseUrl ?? '';
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));

  async function request(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown, retry = true): Promise<unknown> {
    const headers: Record<string, string> = { Accept: 'application/json', ...(await opts.getAuthHeaders()) };
    if (opts.getLocale) headers['Accept-Language'] = opts.getLocale();
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetchImpl(`${base}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    if (res.status === 401) {
      if (retry && opts.tryRefresh && (await opts.tryRefresh())) return request(method, path, body, false);
      opts.onUnauthorized?.();
      throw await toApiError(res);
    }
    if (!res.ok) throw await toApiError(res);
    if (res.status === 204) return undefined;
    return res.json();
  }

  return {
    health: async () => (await request('GET', '/v1/health')) as HealthResponse,
    me: async () => MeResponseSchema.parse(await request('GET', '/v1/me')),
    updatePreferences: async (patch) => {
      const raw = (await request('PUT', '/v1/me/preferences', patch)) as { preferences?: unknown };
      return UserPreferencesSchema.parse(raw.preferences ?? raw);
    },
    chat: async (req) => lenientChatResponse(await request('POST', '/v1/chat', req)),
    confirm: async (req) => lenientChatResponse(await request('POST', '/v1/chat/confirm', req)),
    tool: async (name, input) => {
      const raw = await request('POST', `/v1/tools/${encodeURIComponent(name)}`, input);
      return TOOLS[name].output.parse(raw) as ToolOutput<typeof name>;
    },
    transcribeSession: async (req) => TranscribeSessionResponseSchema.parse(await request('POST', '/v1/voice/transcribe-session', req)),
    sessionReport: async (report) => {
      const raw = (await request('POST', '/v1/voice/session-report', report)) as { ok?: unknown } | undefined;
      return { ok: raw?.ok === true || raw === undefined };
    },
    speak: async (req) => SpeakResponseSchema.parse(await request('POST', '/v1/voice/speak', req)),
    fhirPatient: async (id) => (await request('GET', `/v1/fhir/Patient/${encodeURIComponent(id)}`)) as Record<string, unknown>,
    fhirEverything: async (id) => (await request('GET', `/v1/fhir/Patient/${encodeURIComponent(id)}/$everything`)) as Record<string, unknown>,
  };
}
